import { useEffect, useRef } from "react";
import { FireServices } from "../../../../../shared/services/api/fire/FireServices";
import { showToastHelper } from "../../../quote/components/utils/showToastHelper";
import { calculateEndDate } from "../../utils";
import { detectCoverageType } from "./useCoverageTypeFields";

interface UseFireQuotationLoaderParams {
	mode: "create" | "edit";
	quotationId?: string;
	clientId: string | null;
	setClientId: (id: string | null) => void;
	setClientName: (name: string | null) => void;
	formRef: any;
	toastHandler: any;
	TOKIO_ID: string;
	propertyType: string;
	setFireQuoteData: (d: any) => void;
	setPropertyType: (v: string) => void;
	setOccupationId: (v: string) => void;
	setAssistanceId: (v: string | null) => void;
	setBrokerageIdCode: (v: string) => void;
	setBrokerageComissionPercent: (v: number) => void;
	setPropertysMap: (v: any[]) => void;
	setOccupationsMap: (v: any[]) => void;
	setRawGroups: (v: any[]) => void;
	setLoadingData: (v: boolean) => void;
	setError: (v: string | null) => void;
	setRentValue: (v: string) => void;
	setProlaboreValue: (v: string) => void; // 🆕
	setPredialPercent: (v: string) => void;
	setContentPercent: (v: string) => void;
	setIsLoaderReady: (v: boolean) => void; // 🆕
}

export function useFireQuotationLoader(params: UseFireQuotationLoaderParams) {
	const {
		mode,
		quotationId,
		clientId,
		setClientId,
		setClientName,
		formRef,
		toastHandler,
		TOKIO_ID,
		propertyType,
		setFireQuoteData,
		setPropertyType,
		setOccupationId,
		setAssistanceId,
		setBrokerageIdCode,
		setBrokerageComissionPercent,
		setPropertysMap,
		setOccupationsMap,
		setRawGroups,
		setLoadingData,
		setError,
		setRentValue,
		setProlaboreValue, // 🆕
		setPredialPercent,
		setContentPercent,
		setIsLoaderReady, // 🆕
	} = params;

	// ===============================================================
	// 1. EDIT MODE — Carregar cotação existente
	// ===============================================================
	useEffect(() => {
		if (mode !== "edit" || !quotationId) return;
		setIsLoaderReady(false);

		const loadEditQuote = async () => {
			try {
				setLoadingData(true);

				const quote = await FireServices.getFireQuoteByID(quotationId);
				if (!quote) {
					setError("Cotação não encontrada.");
					showToastHelper(toastHandler, "Erro no carregamento da cotação.", "error");
					return;
				}

				setFireQuoteData(quote);
				setClientId(quote.clientId);
				setClientName(quote.client.name);

				// Valores controlados pelo React
				setRentValue(quote.rent_value);
				setProlaboreValue(String(quote.prolabore_percent_amount ?? "0"));

				// Percentuais predial/conteúdo para o HOOK
				const predial = String(quote.pc_predial ?? "80").replace(".", ",");
				const content = String(quote.pc_content ?? "20").replace(".", ",");

				setPredialPercent(predial);
				setContentPercent(content);

				// Demais campos antes do loadDependencies
				setPropertyType(quote.occupation.riskGroup.id);
				setOccupationId(quote.occupationId);
				setAssistanceId(quote.assistanceId);
				formRef.current?.setFieldValue("assistanceId", quote.assistanceId);

				setBrokerageIdCode(quote.client.brokerageIdCode);
				setBrokerageComissionPercent(quote.brokerage_comission_percent_amount ?? 0);

				// Datas
				const start = quote.validity_start?.substring(0, 10);
				const end = quote.validity_end?.substring(0, 10) || calculateEndDate(start, quote.monthly_validity || 12);

				// Preencher apenas os campos que pertencem ao Unform
				formRef.current?.setData({
					clientId: quote.clientId,
					insurance_companiesId: quote.insurance_companiesId,
					propertyType: quote.occupation.riskGroup.id,
					assistanceId: quote.assistanceId,
					occupationId: quote.occupationId,
					address_zipcode: quote.address_zipcode,
					address_street: quote.address_street,
					address_number: quote.address_number,
					address_complement: quote.address_complement,
					address_district: quote.address_district,
					address_city: quote.address_city,
					address_state: quote.address_state,
					rentValue: quote.rentValue,
					validity_start: start,
					validity_end: end,
					tenantName: quote.tenant_name,
					tenantDocument: quote.tenant_document,
					tenantBirthDate: quote.tenant_birth_date,
					tenantEmail: quote.tenant_email,
					tenantMobilePhone: quote.tenant_mobile_phone,
					lessorName: quote.lessor_name,
					lessorDocument: quote.lessor_document,
					lessorEmail: quote.lessor_email,
					monthly_validity: quote.monthly_validity,
					installment_number: quote.installment_number,
					rent_value: quote.rent_value,
					prolabore_percent_amount: quote.prolabore_percent_amount,
					PredialPercent: predial,
					ContentPercent: content,
					coverageType: detectCoverageType(predial, content),
				});
				setIsLoaderReady(true);

			} catch (e: any) {
				setError(e?.message || "Erro ao carregar cotação.");
			} finally {
				setLoadingData(false);
			}
		};

		loadEditQuote();
	}, [mode, quotationId]);


	// ===============================================================
	// 2. CREATE MODE — Carregar dados padrão do cliente
	// ===============================================================
	useEffect(() => {
		if (mode !== "create" || !clientId) return;

		const loadNewDefault = async () => {
			const clientDefault = await FireServices.getAllClientDataById(clientId, TOKIO_ID);

			// Pode vir como { data: [...] } ou direto como array
			const data =
				(clientDefault as any)?.data?.[0] ??
				(Array.isArray(clientDefault) ? clientDefault[0] : undefined);

			if (!data) return;

			// Comissão e corretora
			setBrokerageComissionPercent(Number(data.comission_insurance_companies ?? 0));
			setBrokerageIdCode(data.brokerage_code);

			// Prolabore default (não mexi no naming!)
			const prolabore = String(data.default_prolabore_percent_amount ?? "0");
			setProlaboreValue(prolabore);

			// ⚠ AQUI estava o problema: nomes diferentes
			const predialRaw = data.default_pc_predial ?? data.pc_predio ?? "80.00";
			const contentRaw = data.default_pc_content ?? data.pc_conteudo ?? "20.00";

			const predial = String(predialRaw).replace(".", ",");
			const content = String(contentRaw).replace(".", ",");

			setPredialPercent(predial);
			setContentPercent(content);

			const start = new Date().toISOString().substring(0, 10);
			const end = calculateEndDate(start, 12);

			formRef.current?.setData({
				insurance_companiesId: TOKIO_ID,
				prolabore_percent_amount: data.default_prolabore_percent_amount,
				installment_number: data.installment_number,
				PredialPercent: predial,
				ContentPercent: content,
				coverageType: detectCoverageType(predial, content),
				validity_start: start,
				validity_end: end,
				monthly_validity: 12,
			});

			// Se você quiser usar o mesmo flag para "tudo carregado"
			setIsLoaderReady(true);
		};

		loadNewDefault();
	}, [mode, clientId]);


	// useEffect(() => {
	// 	if (mode !== "create" || !clientId) return;

	// 	const loadNewDefault = async () => {
	// 		const clientDefault = await FireServices.getAllClientDataById(clientId, TOKIO_ID);
	// 		const data = clientDefault.data?.[0];

	// 		if (data) {
	// 			// Mantém exatamente o que já existia
	// 			setBrokerageComissionPercent(Number(data.comission_insurance_companies ?? 0));
	// 			setBrokerageIdCode(data.brokerage_code);
	// 			setProlaboreValue(String(data.default_prolabore_percent_amount ?? "0"));

	// 			const predial = String(data.default_pc_predial ?? "80").replace(".", ",");
	// 			const content = String(data.default_pc_content ?? "20").replace(".", ",");

	// 			setPredialPercent(predial);
	// 			setContentPercent(content);

	// 			// Datas
	// 			const start = new Date().toISOString().substring(0, 10);
	// 			const end = calculateEndDate(start, 12);

	// 			// Preenche Unform usando os NOMES CORRETOS
	// 			formRef.current?.setData({
	// 				insurance_companiesId: TOKIO_ID,
	// 				prolabore_percent_amount: data.default_prolabore_percent_amount,
	// 				installment_number: data.installment_number,
	// 				PredialPercent: predial,
	// 				ContentPercent: content,
	// 				coverageType: detectCoverageType(predial, content),

	// 				validity_start: start,
	// 				validity_end: end,
	// 				monthly_validity: 12,
	// 			});

	// 			// importantíssimo para coverageType resolver corretamente
	// 			setIsLoaderReady(true);
	// 		}
	// 	};

	// 	loadNewDefault();
	// }, [mode, clientId]);


	// ===============================================================
	// 3. Carregar grupos de risco e ocupações (independente do modo)
	// ===============================================================
	const isInitialLoadRef = useRef(true);

	useEffect(() => {
		if (!clientId) return;

		const loadDependencies = async () => {
			try {
				const result = await FireServices.getGroupRisk();
				if (!result?.data) return;

				const groups = result.data;
				setRawGroups(groups);

				setPropertysMap(
					groups.map((p: any) => ({
						value: p.id,
						label: p.description,
					}))
				);

				const formData = formRef.current?.getData() || {};
				const currentProperty = formData.propertyType;
				const currentOccupation = formData.occupationId;

				let selectedGroup = groups.find((g: any) => g.id === currentProperty);

				// CREATE → Selecionar primeiro group se não tiver
				if (mode === "create" && !currentProperty) {
					const first = groups[0];
					if (first) {
						selectedGroup = first;
						setPropertyType(first.id);
						formRef.current?.setFieldValue("propertyType", first.id);
					}
				}

				if (!selectedGroup) return;

				const rawOc = selectedGroup.ocupations;
				const ocArray = Array.isArray(rawOc) ? rawOc : rawOc ? [rawOc] : [];

				const occupations = ocArray.map((o: any) => ({
					value: o.id,
					label: o.name,
				}));
				setOccupationsMap(occupations);

				const shouldAutoSelectOccupation =
					mode === "create" ||
					(mode === "edit" && !isInitialLoadRef.current) ||
					(currentOccupation && !ocArray.some((o: any) => o.id === currentOccupation));

				if (shouldAutoSelectOccupation && occupations.length > 0) {
					const firstOcc = occupations[0];
					setOccupationId(firstOcc.value);
					formRef.current?.setFieldValue("occupationId", firstOcc.value);
				}

				if (mode === "edit") {
					isInitialLoadRef.current = false;
				}
			} catch (e) {
				console.error("Erro ao carregar dependências:", e);
				showToastHelper(toastHandler, "Dados incompletos para salvar.", "error");
			}
		};

		loadDependencies();
	}, [clientId, propertyType]);
}
